import { useEffect, useState } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { MapContainer, TileLayer, CircleMarker, Polyline, Popup, Circle } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { KeyRound, Printer, BellRing, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { PageHeader, TableWrap, Empty, th, td, tr } from "@/components/common";

const TABS = [["access", "Driver App Access"], ["map", "Live Map"], ["playback", "Route Playback"], ["alerts", "Alert Hub"], ["places", "Allowed Places"]];
const INDIA = [22.5, 79];
const ago = (s) => { if (!s) return "never"; const m = Math.round((Date.now() - new Date(s)) / 60000); return m < 60 ? `${m} min ago` : `${Math.floor(m / 60)} h ${m % 60} m ago`; };
const tiles = <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />;
const appUrl = (t) => `${window.location.origin}/track/${t}`;

function CredDialog({ cred, onClose }) {
  if (!cred) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="cred-dialog">
        <DialogHeader><DialogTitle>{cred.name} — Driver App Login</DialogTitle></DialogHeader>
        <div id="cred-print" className="space-y-3 text-center">
          <QRCodeSVG value={appUrl(cred.token)} size={200} className="mx-auto" data-testid="cred-qr" />
          <p className="text-xs text-muted-foreground">Phone camera se scan karein — app turant khulegi</p>
          <div className="rounded-lg border p-3 text-sm"><div>Login ID: <b data-testid="cred-login">{cred.login_id}</b></div>
            {cred.pin && <div>Passcode: <b data-testid="cred-pin">{cred.pin}</b></div>}<div className="mt-1 text-xs text-muted-foreground">{window.location.origin}/track</div></div>
          {cred.pin && <p className="text-[11px] text-amber-700">Passcode sirf abhi dikh raha hai — note ya print kar lein.</p>}
        </div>
        <Button variant="outline" data-testid="cred-print-btn" onClick={() => window.print()}><Printer className="mr-1 h-4 w-4" />Print QR</Button>
      </DialogContent>
    </Dialog>
  );
}

function Access({ drivers, reload }) {
  const [cred, setCred] = useState(null);
  const make = async (d) => {
    if (d.has_app && !window.confirm("Naya Login / QR banane se purana link band ho jaayega. Aage badhein?")) return;
    try { const { data } = await api.post(`/driver-app/drivers/${d.id}/credentials`); setCred({ ...data, name: d.name }); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (d, v) => { try { await api.patch(`/driver-app/drivers/${d.id}/tracking`, { enabled: v }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  if (!drivers.length) return <Empty testid="app-drivers-empty" text="Driver Master me koi driver nahi" />;
  return (
    <>
      <TableWrap testid="app-drivers-table"><table className="w-full text-sm">
        <thead><tr>{["Driver", "Vehicle / Trip", "App Login", "Last Ping", "Tracking", ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>{drivers.map((d) => (
          <tr key={d.id} className={tr} data-testid={`app-driver-${d.id}`}>
            <td className={td}><b>{d.name}</b><div className="text-xs text-muted-foreground">{d.phone}</div></td>
            <td className={td}>{d.vehicle_number || "—"}<div className="text-xs text-muted-foreground">{d.trip ? `${d.trip.lr_number} · ${d.trip.route}` : "No active trip"}</div></td>
            <td className={`${td} font-mono text-xs`}>{d.app_login_id || "—"}</td>
            <td className={td}>{ago(d.last_ping_at)}{d.gps_on === false && <span className="ml-1 rounded bg-rose-100 px-1 text-[10px] font-bold text-rose-700">GPS OFF</span>}
              {d.battery != null && <div className="text-xs text-muted-foreground">Battery {d.battery}%</div>}</td>
            <td className={td}><Switch data-testid={`tracking-toggle-${d.id}`} checked={d.tracking_enabled} onCheckedChange={(v) => toggle(d, v)} /></td>
            <td className={`${td} text-right`}>
              {d.has_app && <Button size="sm" variant="ghost" data-testid={`show-qr-${d.id}`} onClick={() => api.get(`/driver-app/drivers/${d.id}/qr`).then((r) => setCred({ ...r.data, name: d.name })).catch((e) => toast.error(errMsg(e)))}>QR</Button>}
              <Button size="sm" variant="outline" data-testid={`make-cred-${d.id}`} onClick={() => make(d)}><KeyRound className="mr-1 h-3.5 w-3.5" />{d.has_app ? "Reset" : "Create Login / QR"}</Button>
            </td>
          </tr>
        ))}</tbody>
      </table></TableWrap>
      <CredDialog cred={cred} onClose={() => setCred(null)} />
    </>
  );
}

function LiveMap({ drivers }) {
  const pts = drivers.filter((d) => d.lat != null);
  const fresh = (d) => d.last_ping_at && Date.now() - new Date(d.last_ping_at) < 15 * 60000;
  return (
    <div className="card-base overflow-hidden" data-testid="live-map">
      <div className="px-4 py-2 text-xs text-muted-foreground">{pts.length} gaadi map par · hara = 15 min me ping aaya · laal = purana · har 60 sec refresh</div>
      <MapContainer center={pts[0] ? [pts[0].lat, pts[0].lng] : INDIA} zoom={pts.length ? 7 : 5} style={{ height: 520 }}>
        {tiles}
        {pts.map((d) => (
          <CircleMarker key={d.id} center={[d.lat, d.lng]} radius={9} pathOptions={{ color: fresh(d) ? "#059669" : "#e11d48", fillOpacity: 0.8 }}>
            <Popup><b>{d.vehicle_number || "—"}</b><br />{d.name} · {d.phone}<br />Speed {d.speed ?? 0} km/h<br />Last ping {ago(d.last_ping_at)}{d.trip && <><br />{d.trip.lr_number} · {d.trip.route}</>}</Popup>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
}

function Playback() {
  const [trips, setTrips] = useState([]);
  const [bid, setBid] = useState("");
  const [r, setR] = useState(null);
  const [i, setI] = useState(0);
  useEffect(() => { api.get("/driver-app/trips").then((x) => setTrips(x.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!bid) { setR(null); return; }
    api.get(`/driver-app/trips/${bid}/route`).then((x) => { setR(x.data); setI(x.data.points.length - 1); }).catch((e) => toast.error(errMsg(e)));
  }, [bid]);
  const line = r ? r.points.map((p) => [p.lat, p.lng]) : [];
  const cur = r?.points[i];
  return (
    <div className="space-y-3">
      <select className="native-select h-10 max-w-xl" data-testid="playback-trip" value={bid} onChange={(e) => setBid(e.target.value)}>
        <option value="">Trip chunein ({trips.length} trips me GPS data)</option>
        {trips.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
      </select>
      {r && (
        <>
          <div className="flex flex-wrap gap-4 text-sm" data-testid="playback-stats"><span>Distance: <b>{r.distance_km} km</b></span><span>Points: <b>{r.points.length}</b></span>
            <span>Halts (15+ min): <b>{r.halts.length}</b></span><span>{r.start ? `${new Date(r.start).toLocaleString("en-IN")} → ${new Date(r.end).toLocaleString("en-IN")}` : "No GPS data"}</span></div>
          {r.points.length > 0 && <input type="range" min={0} max={r.points.length - 1} value={i} onChange={(e) => setI(Number(e.target.value))} className="w-full" data-testid="playback-slider" />}
          {cur && <div className="text-xs text-muted-foreground">{new Date(cur.ts).toLocaleString("en-IN")} · {cur.speed ?? 0} km/h</div>}
          <div className="card-base overflow-hidden">
            <MapContainer key={bid} center={line[0] || INDIA} zoom={line.length ? 9 : 5} style={{ height: 480 }}>
              {tiles}
              {line.length > 1 && <Polyline positions={line.slice(0, i + 1)} pathOptions={{ color: "#0ea5e9", weight: 4 }} />}
              {r.halts.map((h, k) => <CircleMarker key={k} center={[h.lat, h.lng]} radius={7} pathOptions={{ color: "#f59e0b" }}><Popup>Halt {h.minutes} min<br />{new Date(h.start).toLocaleString("en-IN")}</Popup></CircleMarker>)}
              {cur && <CircleMarker center={[cur.lat, cur.lng]} radius={10} pathOptions={{ color: "#111827", fillOpacity: 1 }} />}
            </MapContainer>
          </div>
        </>
      )}
    </div>
  );
}

function Alerts({ list, reload }) {
  const ack = async (a) => { await api.post(`/driver-app/alerts/${a.id}/ack`); reload(); };
  const label = { gps_off: "GPS Band", offline: "Internet / App Off", halt: "Galat jagah ruki" };
  if (!list.length) return <Empty testid="alerts-empty" text="Koi alert nahi" />;
  return (
    <div className="space-y-2" data-testid="alert-hub">
      {list.map((a) => (
        <div key={a.id} className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 text-sm ${a.active ? "border-rose-200 bg-rose-50 dark:bg-rose-950/30" : "opacity-60"}`} data-testid={`alert-${a.id}`}>
          <div><b className="mr-2 rounded bg-rose-600 px-1.5 py-0.5 text-[10px] text-white">{label[a.type]}</b>{a.message}
            <div className="text-xs text-muted-foreground">{a.lr_number ? `LR ${a.lr_number} · ` : ""}{a.phone} · since {new Date(a.since).toLocaleString("en-IN")}{a.active ? "" : " · resolved"}</div></div>
          {!a.ack ? <Button size="sm" variant="outline" data-testid={`ack-${a.id}`} onClick={() => ack(a)}>Seen</Button> : <span className="text-xs">Seen by {a.ack_by}</span>}
        </div>
      ))}
    </div>
  );
}

function Places() {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ name: "", lat: "", lng: "", radius_m: "1000" });
  const load = () => api.get("/driver-app/geofences").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    const lat = parseFloat(f.lat), lng = parseFloat(f.lng);
    if (!f.name.trim() || Number.isNaN(lat) || Number.isNaN(lng)) { toast.error("Name, Latitude aur Longitude bharein"); return; }
    try { await api.post("/driver-app/geofences", { name: f.name, lat, lng, radius_m: Number(f.radius_m) }); setF({ name: "", lat: "", lng: "", radius_m: "1000" }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (g) => { if (window.confirm(`Delete ${g.name}?`)) { await api.delete(`/driver-app/geofences/${g.id}`); load(); } };
  const here = () => navigator.geolocation?.getCurrentPosition((p) => setF((x) => ({ ...x, lat: p.coords.latitude.toFixed(6), lng: p.coords.longitude.toFixed(6) })));
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Trip ke Loading aur Unloading shahar (5 km tak) apne aap allowed hain. Pump, dhaba, yard jaisi aur jagah yahan jodein ("My location" se wahan khade hokar turant bhar sakte hain).</p>
      <div className="grid gap-2 sm:grid-cols-5">
        <Input placeholder="Name (e.g. Indore Yard)" data-testid="geo-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Input placeholder="Latitude" data-testid="geo-lat" value={f.lat} onChange={(e) => setF({ ...f, lat: e.target.value })} />
        <Input placeholder="Longitude" data-testid="geo-lng" value={f.lng} onChange={(e) => setF({ ...f, lng: e.target.value })} />
        <Input placeholder="Radius (m)" data-testid="geo-radius" value={f.radius_m} onChange={(e) => setF({ ...f, radius_m: e.target.value })} />
        <div className="flex gap-1"><Button variant="outline" size="sm" className="h-10" data-testid="geo-here" onClick={here}>My location</Button><Button className="h-10" data-testid="geo-add" onClick={add}>Add</Button></div>
      </div>
      <div className="card-base overflow-hidden">
        <MapContainer center={list[0] ? [list[0].lat, list[0].lng] : INDIA} zoom={list.length ? 8 : 5} style={{ height: 380 }}>
          {tiles}
          {list.map((g) => <Circle key={g.id} center={[g.lat, g.lng]} radius={g.radius_m} pathOptions={{ color: "#10b981" }}><Popup>{g.name}</Popup></Circle>)}
        </MapContainer>
      </div>
      {list.map((g) => (
        <div key={g.id} className="flex items-center justify-between rounded border px-3 py-2 text-sm" data-testid={`geo-${g.id}`}>
          <span><b>{g.name}</b> · {g.lat}, {g.lng} · {g.radius_m} m</span>
          <button data-testid={`geo-del-${g.id}`} onClick={() => del(g)} className="text-rose-600"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
  );
}

export default function DriverApp() {
  const [tab, setTab] = useState("access");
  const [drivers, setDrivers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const load = () => api.get("/driver-app/drivers").then((r) => setDrivers(r.data)).catch((e) => toast.error(errMsg(e)));
  const loadAlerts = () => api.get("/driver-app/alerts").then((r) => {
    setAlerts((old) => {
      const seen = new Set(old.map((a) => a.id));
      r.data.filter((a) => a.active && !a.ack && old.length && !seen.has(a.id)).forEach((a) => toast.error(a.message, { duration: 15000 }));
      return r.data;
    });
  }).catch(() => {});
  useEffect(() => { load(); loadAlerts(); const t = setInterval(() => { load(); loadAlerts(); }, 60000); return () => clearInterval(t); }, []);
  const open = alerts.filter((a) => a.active && !a.ack).length;
  return (
    <div className="space-y-6">
      <PageHeader title="Driver App & Tracking" subtitle="Driver ko QR / passcode do, live GPS dekho, trip ka rasta replay karo" />
      {open > 0 && <button onClick={() => setTab("alerts")} className="flex w-full items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm font-semibold text-rose-800" data-testid="alert-banner"><BellRing className="h-4 w-4" />{open} tracking alert pending — dekhein</button>}
      <div className="flex flex-wrap gap-1.5">{TABS.map(([k, l]) => (
        <Button key={k} size="sm" variant={tab === k ? "default" : "outline"} data-testid={`da-tab-${k}`} onClick={() => setTab(k)}>{l}{k === "alerts" && open ? ` (${open})` : ""}</Button>
      ))}</div>
      {tab === "access" && <Access drivers={drivers} reload={load} />}
      {tab === "map" && <LiveMap drivers={drivers} />}
      {tab === "playback" && <Playback />}
      {tab === "alerts" && <Alerts list={alerts} reload={loadAlerts} />}
      {tab === "places" && <Places />}
    </div>
  );
}
